/**
 * React hook for module management
 *
 * Integrates ModuleLoader, ModuleRuntime, and TrustStore for
 * loading, verifying, and running AOS modules.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  TrustStore,
  ModuleLoader,
  ModuleRuntime,
  DAGStore,
  Identity,
  LoadedModule,
  ModulePermission,
  ModuleLoadResult,
  checkPermissions,
  PERMISSION_CATEGORIES,
  type PermissionCheckResult,
  type ModuleExecutionResult,
} from '@amorphous/core';

// ============================================================================
// Types
// ============================================================================

/**
 * Installed module record
 */
export interface InstalledModule {
  /** Manifest hash (module identifier in DAG) */
  manifestHash: string;
  /** Module ID from manifest */
  moduleId: string;
  /** Module name */
  name: string;
  /** Module version */
  version: string;
  /** Author public key */
  authorPubkey: string;
  /** Granted permissions */
  grantedPermissions: ModulePermission[];
  /** Trusted signers who signed this module */
  trustedSigners: string[];
  /** When installed */
  installedAt: number;
  /** Whether currently running */
  running: boolean;
}

/**
 * Module pending permission approval
 */
export interface PendingModule {
  /** Loaded module data */
  module: LoadedModule;
  /** Manifest hash */
  manifestHash: string;
  /** Permission check result */
  permissionCheck: PermissionCheckResult;
  /** Callback when approved */
  onApprove: (permissions: ModulePermission[]) => void;
  /** Callback when denied */
  onDeny: () => void;
}

/**
 * useModules hook result
 */
export interface UseModulesResult {
  /** Installed modules */
  installedModules: InstalledModule[];
  /** Currently running modules */
  runningModules: string[];
  /** Module pending permission approval */
  pendingModule: PendingModule | null;
  /** Loading state */
  loading: boolean;
  /** Error state */
  error: Error | null;
  /** Load a module from DAG (starts permission flow) */
  loadModule: (manifestHash: string) => Promise<ModuleLoadResult>;
  /** Run an installed module */
  runModule: (manifestHash: string) => Promise<ModuleExecutionResult>;
  /** Stop a running module */
  stopModule: (moduleId: string) => boolean;
  /** Uninstall a module */
  uninstallModule: (manifestHash: string) => void;
  /** Add a trusted key */
  addTrustedKey: (pubkey: string, name?: string) => void;
  /** Remove a trusted key */
  removeTrustedKey: (pubkey: string) => void;
  /** Get trusted keys */
  getTrustedKeys: () => string[];
  /** Approve pending module with selected permissions */
  approvePending: (permissions: ModulePermission[]) => void;
  /** Deny pending module */
  denyPending: () => void;
}

// ============================================================================
// Storage Keys
// ============================================================================

const STORAGE_KEY_INSTALLED = 'aos:installed-modules';
const STORAGE_KEY_TRUSTED = 'aos:trusted-keys';

// ============================================================================
// Hook Implementation
// ============================================================================

export function useModules(
  dagStore: DAGStore,
  identity?: Identity | null
): UseModulesResult {
  // State
  const [installedModules, setInstalledModules] = useState<InstalledModule[]>([]);
  const [runningModules, setRunningModules] = useState<string[]>([]);
  const [pendingModule, setPendingModule] = useState<PendingModule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Refs for stable instances
  const trustStoreRef = useRef<TrustStore | null>(null);
  const loaderRef = useRef<ModuleLoader | null>(null);
  const runtimeRef = useRef<ModuleRuntime | null>(null);

  // Initialize trust store, loader, and runtime
  useEffect(() => {
    // Create trust store
    const trustStore = new TrustStore({
      allowUserAnchors: true,
      minTrustedSignatures: 1,
    });

    // Load trusted keys from storage
    try {
      const stored = localStorage.getItem(STORAGE_KEY_TRUSTED);
      if (stored) {
        const keys = JSON.parse(stored) as { pubkey: string; name: string }[];
        for (const { pubkey, name } of keys) {
          trustStore.addAnchor({
            type: 'user',
            name: name || pubkey.slice(0, 8),
            pubkeys: [pubkey],
            added: Date.now(),
          });
        }
      }
    } catch (err) {
      console.warn('[Modules] Failed to load trusted keys:', err);
    }

    // If user has identity, trust their own key
    if (identity) {
      trustStore.addAnchor({
        type: 'user',
        name: 'self',
        pubkeys: [identity.publicKey],
        added: Date.now(),
      });
    }

    trustStoreRef.current = trustStore;

    // Create module loader
    const loader = new ModuleLoader({
      trustStore,
      dagStore,
      maxModuleSize: 10 * 1024 * 1024, // 10MB
      allowUnknownPermissions: false,
    });
    loaderRef.current = loader;

    // Create module runtime
    const runtime = new ModuleRuntime({
      dagStore,
      identity: identity?.getKeyPair(),
      onLog: (moduleId, level, args) => {
        const prefix = `[Module:${moduleId}]`;
        switch (level) {
          case 'debug':
            console.debug(prefix, ...args);
            break;
          case 'info':
            console.info(prefix, ...args);
            break;
          case 'warn':
            console.warn(prefix, ...args);
            break;
          case 'error':
            console.error(prefix, ...args);
            break;
        }
      },
      onSignRequest: async (moduleId, _data) => {
        // TODO: Show signing approval dialog
        return window.confirm(
          `Module "${moduleId}" wants to sign data. Allow?`
        );
      },
    });
    runtimeRef.current = runtime;

    // Load installed modules from storage
    try {
      const stored = localStorage.getItem(STORAGE_KEY_INSTALLED);
      if (stored) {
        const modules = JSON.parse(stored) as InstalledModule[];
        setInstalledModules(modules);
      }
    } catch (err) {
      console.warn('[Modules] Failed to load installed modules:', err);
    }

    setLoading(false);

    return () => {
      // Stop all running modules on cleanup
      if (runtimeRef.current) {
        for (const moduleId of runtimeRef.current.getRunningModules().keys()) {
          runtimeRef.current.stop(moduleId);
        }
      }
    };
  }, [dagStore, identity]);

  // Persist installed modules
  const persistInstalled = useCallback((modules: InstalledModule[]) => {
    try {
      localStorage.setItem(STORAGE_KEY_INSTALLED, JSON.stringify(modules));
    } catch (err) {
      console.warn('[Modules] Failed to persist installed modules:', err);
    }
  }, []);

  // Persist trusted keys
  const persistTrustedKeys = useCallback(() => {
    if (!trustStoreRef.current) return;
    try {
      const anchors = trustStoreRef.current.getAnchors();
      const keys = anchors
        .filter(a => a.type === 'user' && a.name !== 'self')
        .flatMap(a => a.pubkeys.map(pubkey => ({ pubkey, name: a.name })));
      localStorage.setItem(STORAGE_KEY_TRUSTED, JSON.stringify(keys));
    } catch (err) {
      console.warn('[Modules] Failed to persist trusted keys:', err);
    }
  }, []);

  // Load module from DAG
  const loadModule = useCallback(async (manifestHash: string): Promise<ModuleLoadResult> => {
    if (!loaderRef.current) {
      return {
        success: false,
        errors: ['Module loader not initialized'],
        warnings: [],
      };
    }

    try {
      setError(null);
      const result = await loaderRef.current.load(manifestHash);

      if (!result.success || !result.module) {
        return result;
      }

      // Check if already installed
      const existing = installedModules.find(m => m.manifestHash === manifestHash);
      if (existing) {
        // Already installed, just return success
        return result;
      }

      // Check permissions
      const permCheck = result.permissionCheck || checkPermissions(result.module.manifest.permissions);

      // If all permissions are safe, auto-approve
      const allSafe = result.module.manifest.permissions.every(
        p => PERMISSION_CATEGORIES[p] === 'safe'
      );

      if (allSafe) {
        // Auto-install with all permissions
        const installed: InstalledModule = {
          manifestHash,
          moduleId: result.module.manifest.id,
          name: result.module.manifest.name,
          version: result.module.manifest.version,
          authorPubkey: result.module.manifest.author.pubkey,
          grantedPermissions: result.module.manifest.permissions,
          trustedSigners: result.module.trustedSigners,
          installedAt: Date.now(),
          running: false,
        };

        const updated = [...installedModules, installed];
        setInstalledModules(updated);
        persistInstalled(updated);

        // Grant permissions in loader
        loaderRef.current.grantPermissions(manifestHash, result.module.manifest.permissions);

        return result;
      }

      // Need permission approval - set pending
      return new Promise((resolve) => {
        setPendingModule({
          module: result.module!,
          manifestHash,
          permissionCheck: permCheck,
          onApprove: (permissions) => {
            // Install with approved permissions
            const installed: InstalledModule = {
              manifestHash,
              moduleId: result.module!.manifest.id,
              name: result.module!.manifest.name,
              version: result.module!.manifest.version,
              authorPubkey: result.module!.manifest.author.pubkey,
              grantedPermissions: permissions,
              trustedSigners: result.module!.trustedSigners,
              installedAt: Date.now(),
              running: false,
            };

            const updated = [...installedModules, installed];
            setInstalledModules(updated);
            persistInstalled(updated);

            // Grant permissions in loader
            loaderRef.current?.grantPermissions(manifestHash, permissions);

            setPendingModule(null);
            resolve(result);
          },
          onDeny: () => {
            setPendingModule(null);
            resolve({
              success: false,
              errors: ['User denied permissions'],
              warnings: [],
            });
          },
        });
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      return {
        success: false,
        errors: [error.message],
        warnings: [],
      };
    }
  }, [installedModules, persistInstalled]);

  // Run an installed module
  const runModule = useCallback(async (manifestHash: string): Promise<ModuleExecutionResult> => {
    if (!runtimeRef.current || !loaderRef.current) {
      return {
        success: false,
        error: 'Runtime not initialized',
      };
    }

    const installed = installedModules.find(m => m.manifestHash === manifestHash);
    if (!installed) {
      return {
        success: false,
        error: 'Module not installed',
      };
    }

    // Get loaded module from loader
    let loadedModule = loaderRef.current.getLoaded(manifestHash);
    if (!loadedModule) {
      // Need to reload
      const loadResult = await loaderRef.current.load(manifestHash);
      if (!loadResult.success || !loadResult.module) {
        return {
          success: false,
          error: loadResult.errors.join(', '),
        };
      }
      loadedModule = loadResult.module;
      // Restore granted permissions
      loaderRef.current.grantPermissions(manifestHash, installed.grantedPermissions);
    }

    // Update loaded module with granted permissions
    loadedModule.grantedPermissions = installed.grantedPermissions;

    // Execute
    const result = await runtimeRef.current.execute(loadedModule);

    if (result.success) {
      // Update running state
      setRunningModules(prev => [...prev, installed.moduleId]);
      setInstalledModules(prev =>
        prev.map(m =>
          m.manifestHash === manifestHash ? { ...m, running: true } : m
        )
      );
    }

    return result;
  }, [installedModules]);

  // Stop a running module
  const stopModule = useCallback((moduleId: string): boolean => {
    if (!runtimeRef.current) return false;

    const stopped = runtimeRef.current.stop(moduleId);
    if (stopped) {
      setRunningModules(prev => prev.filter(id => id !== moduleId));
      setInstalledModules(prev =>
        prev.map(m =>
          m.moduleId === moduleId ? { ...m, running: false } : m
        )
      );
    }
    return stopped;
  }, []);

  // Uninstall a module
  const uninstallModule = useCallback((manifestHash: string) => {
    const module = installedModules.find(m => m.manifestHash === manifestHash);
    if (!module) return;

    // Stop if running
    if (module.running && runtimeRef.current) {
      runtimeRef.current.stop(module.moduleId);
      setRunningModules(prev => prev.filter(id => id !== module.moduleId));
    }

    // Remove from loader cache
    loaderRef.current?.unload(manifestHash);

    // Remove from installed
    const updated = installedModules.filter(m => m.manifestHash !== manifestHash);
    setInstalledModules(updated);
    persistInstalled(updated);
  }, [installedModules, persistInstalled]);

  // Add trusted key
  const addTrustedKey = useCallback((pubkey: string, name?: string) => {
    if (!trustStoreRef.current) return;

    trustStoreRef.current.addAnchor({
      type: 'user',
      name: name || pubkey.slice(0, 8),
      pubkeys: [pubkey],
      added: Date.now(),
    });

    persistTrustedKeys();
  }, [persistTrustedKeys]);

  // Remove trusted key
  const removeTrustedKey = useCallback((pubkey: string) => {
    if (!trustStoreRef.current) return;

    // Find anchor containing this key
    const anchors = trustStoreRef.current.getAnchors();
    for (const anchor of anchors) {
      if (anchor.pubkeys.includes(pubkey) && anchor.type === 'user' && anchor.name !== 'self') {
        trustStoreRef.current.removeAnchor(anchor.type, anchor.name);
        break;
      }
    }

    persistTrustedKeys();
  }, [persistTrustedKeys]);

  // Get trusted keys
  const getTrustedKeys = useCallback((): string[] => {
    if (!trustStoreRef.current) return [];
    return Array.from(trustStoreRef.current.getTrustedKeys());
  }, []);

  // Approve pending module
  const approvePending = useCallback((permissions: ModulePermission[]) => {
    if (pendingModule) {
      pendingModule.onApprove(permissions);
    }
  }, [pendingModule]);

  // Deny pending module
  const denyPending = useCallback(() => {
    if (pendingModule) {
      pendingModule.onDeny();
    }
  }, [pendingModule]);

  return {
    installedModules,
    runningModules,
    pendingModule,
    loading,
    error,
    loadModule,
    runModule,
    stopModule,
    uninstallModule,
    addTrustedKey,
    removeTrustedKey,
    getTrustedKeys,
    approvePending,
    denyPending,
  };
}
